'use client'

import { createContext, PropsWithChildren, useContext, useState } from 'react'
import { createStore, StoreApi, useStore } from 'zustand'

import { createDialogStore, type DialogState } from '@/shared/store'

export type OpenOptions = {
  closeOnOpen?: boolean
}

const DialogStoreContext = createContext<StoreApi<DialogState> | null>(null)

export const DialogProvider = ({ children }: PropsWithChildren) => {
  const [store] = useState(() => createStore(createDialogStore))

  return (
    <DialogStoreContext.Provider value={store}>
      {children}
    </DialogStoreContext.Provider>
  )
}

const useDialogContext = <T,>(selector: (state: DialogState) => T) => {
  const store = useContext(DialogStoreContext)

  if (!store) {
    throw new Error('useDialogContext must be used within a DialogProvider')
  }

  return useStore(store, selector)
}

export const useDialogState = <T extends string>() => {
  const dialogs = useDialogContext(state => state.dialogs)
  const actions = useDialogContext(state => state.actions)

  const closeHandler = (key: T) => () => {
    actions.close(key)
  }

  return {
    isOpen: (key: T) => dialogs?.[key] || false,
    open: (key: T, options?: OpenOptions) => {
      actions.open(key, options)
    },
    close: (key: T) => {
      actions.close(key)
    },
    closeHandler,
    closeAll: actions.closeAll,
    toggle: (key: T) => {
      actions.toggle(key)
    },
  }
}
